import { useMemo, useState, useEffect } from 'react'
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom'
import {
  Bell,
  CheckSquare,
  CreditCard,
  Crown,
  FileText,
  Layers,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Sliders,
  Sun,
  Users,
  Store,
  Building2,
  Car,
  Wrench,
  PackageOpen,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'

import { PageContainer } from '@/components/common'
import { Brand } from '@/components/layout'
import {
  Avatar,
  Breadcrumb,
  Button,
  Drawer,
  DrawerContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui'
import { appRoutes } from '@/constants'
import { useAuth } from '@/providers/auth-provider'
import { useTheme } from '@/providers/theme-provider'
import { useLogout } from '@/features/auth/hooks'
import { useAdminRequestsCount } from '../../hooks/use-admin'

function useAdminBreadcrumbs() {
  const { pathname } = useLocation()

  return useMemo(() => {
    const segments = pathname.split('/').filter(Boolean)
    const items: { label: string; to: string }[] = [{ label: 'Admin Portal', to: appRoutes.admin.dashboard }]
    
    if (segments.length > 1) {
      let path = appRoutes.admin.root
      for (const segment of segments.slice(1)) {
        path = `${path}/${segment}`
        const labelMap: Record<string, string> = {
          dashboard: 'Dashboard',
          users: 'Users Directory',
          'seller-requests': 'Seller Requests',
          'dealer-requests': 'Dealer Requests',
          subscriptions: 'Subscriptions',
          'plans-management': 'Plans Control',
          'form-builder': 'Form Builder',
          'workflow-settings': 'Listing Workflows',
          'audit-logs': 'Audit Trail',
          payments: 'Payments',
          parts: 'Parts Inventory',
          orders: 'Orders & Dispatch',
          settings: 'Admin Settings',
        }
        items.push({
          label: labelMap[segment] ?? segment.charAt(0).toUpperCase() + segment.slice(1),
          to: path,
        })
      }
    }
    return items
  }, [pathname])
}

export function AdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('admin_sidebar_collapsed')
    return saved === 'true'
  })

  const navigate = useNavigate()
  const location = useLocation()
  const breadcrumbItems = useAdminBreadcrumbs()
  const { user } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const logout = useLogout()
  const { data: requestsCount } = useAdminRequestsCount()

  const toggleCollapse = () => {
    setIsCollapsed((prev) => {
      const next = !prev
      localStorage.setItem('admin_sidebar_collapsed', String(next))
      return next
    })
  }

  const sidebarNavItems = [
    { label: 'Dashboard', to: appRoutes.admin.dashboard, icon: LayoutDashboard },
    {
      label: 'Cars Control Center',
      to: appRoutes.admin.cars.overview,
      icon: Car,
      subItems: [
        { label: 'Cars Overview', to: appRoutes.admin.cars.overview },
        { label: 'Brands', to: appRoutes.admin.cars.brands },
        { label: 'Models', to: appRoutes.admin.cars.models },
        { label: 'Generations', to: appRoutes.admin.cars.generations },
        { label: 'Variants', to: appRoutes.admin.cars.variants },
        { label: 'Feature Catalog', to: appRoutes.admin.cars.features },
        { label: 'Listings / Moderation', to: appRoutes.admin.cars.listings },
        { label: 'Model Requests', to: appRoutes.admin.cars.modelRequests },
      ],
    },
    { label: 'Users', to: appRoutes.admin.users, icon: Users },
    {
      label: 'Seller Requests',
      to: appRoutes.admin.sellerRequests,
      icon: Store,
      badge: requestsCount?.pendingSeller,
    },
    {
      label: 'Dealer Requests',
      to: appRoutes.admin.dealerRequests,
      icon: Building2,
      badge: requestsCount?.pendingDealer,
    },
    { label: 'Subscriptions', to: appRoutes.admin.subscriptions, icon: Crown },
    { label: 'Billing & Gateways', to: appRoutes.admin.billing, icon: CreditCard },
    { label: 'Plans Control', to: appRoutes.admin.plansManagement, icon: Layers },
    { label: 'Form Builder', to: appRoutes.admin.formBuilder, icon: FileText },
    { label: 'Listing Workflows', to: appRoutes.admin.workflowSettings, icon: Sliders },
    { label: 'Audit Trail', to: appRoutes.admin.auditLogs, icon: ShieldCheck },
    { label: 'Payments', to: appRoutes.admin.payments, icon: CreditCard },
    { label: 'Parts Inventory', to: appRoutes.admin.parts, icon: Wrench },
    { label: 'Orders & Dispatch', to: appRoutes.admin.orders, icon: PackageOpen },
    { label: 'Settings', to: appRoutes.admin.settings, icon: Settings },
  ]

  const initials = `${user?.firstName?.[0] ?? 'A'}${user?.lastName?.[0] ?? 'D'}`.toUpperCase()

  const handleLogout = async () => {
    await logout.mutateAsync()
    navigate(appRoutes.auth.login, { replace: true })
  }

  const renderSidebarContent = (isMobile = false) => {
    const collapsed = isMobile ? false : isCollapsed

    return (
      <div className="flex h-full flex-col justify-between">
        {/* Header Brand (Fixed at top) */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 shrink-0">
          <div className="flex items-center gap-2 overflow-hidden">
            <Brand />
            {!collapsed && (
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-primary border border-primary/20 shrink-0">
                Admin
              </span>
            )}
          </div>
          {!isMobile && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={toggleCollapse}
              className="size-7 rounded-lg text-muted-foreground hover:text-foreground shrink-0 hidden lg:flex"
              title={collapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
            >
              {collapsed ? <ChevronRight className="size-4" /> : <ChevronLeft className="size-4" />}
            </Button>
          )}
        </div>

        {/* Scrollable Navigation Links (Middle) */}
        <div className="flex-1 overflow-y-auto minimal-red-scrollbar px-3 py-3 space-y-1">
          <nav className="space-y-1">
            {sidebarNavItems.map((item) => {
              const isActive = location.pathname === item.to || (item.subItems && location.pathname.startsWith(item.to))
              const Icon = item.icon
              return (
                <div key={item.to} className="space-y-1">
                  <Link
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    title={collapsed ? item.label : undefined}
                    className={`group flex items-center ${
                      collapsed ? 'justify-center px-2' : 'justify-between px-3.5'
                    } py-2.5 text-sm font-semibold rounded-xl transition-all ${
                      isActive
                        ? 'bg-primary text-primary-foreground shadow-glow'
                        : 'text-muted-foreground hover:bg-accent/40 hover:text-foreground'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Icon className={`size-4.5 shrink-0 ${isActive ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-foreground'}`} />
                      {!collapsed && <span className="truncate">{item.label}</span>}
                    </div>

                    {!collapsed && item.badge !== undefined && item.badge > 0 && (
                      <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${isActive ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-primary/20 text-primary'}`}>
                        {item.badge}
                      </span>
                    )}
                  </Link>

                  {!collapsed && item.subItems && isActive && (
                    <div className="pl-6 space-y-1 border-l border-border/50 ml-4 py-1">
                      {item.subItems.map((sub) => {
                        const isSubActive = location.pathname === sub.to
                        return (
                          <Link
                            key={sub.to}
                            to={sub.to}
                            onClick={() => setMobileOpen(false)}
                            className={`block px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                              isSubActive
                                ? 'bg-primary/15 text-primary font-bold'
                                : 'text-muted-foreground hover:text-foreground hover:bg-accent/30'
                            }`}
                          >
                            {sub.label}
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </nav>
        </div>

        {/* Footer Admin Profile (Fixed at bottom) */}
        <div className="border-t border-border/60 p-3 space-y-2 shrink-0 bg-card/40 backdrop-blur-md">
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`}>
            <Avatar fallback={initials} alt="Admin Avatar" className="size-9 border border-primary/30 shrink-0" />
            {!collapsed && (
              <div className="min-w-0">
                <p className="truncate text-xs font-bold text-foreground">
                  {user ? `${user.firstName} ${user.lastName}` : 'Platform Admin'}
                </p>
                <p className="truncate text-[10px] text-muted-foreground">{user?.email}</p>
              </div>
            )}
          </div>

          {!collapsed && (
            <span className="block text-[10px] font-medium text-muted-foreground text-center">
              Enterprise ERP Portal v1.0
            </span>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 hidden border-r border-border/60 bg-card/60 backdrop-blur-xl lg:block transition-all duration-300 ${
          isCollapsed ? 'w-20' : 'w-64'
        }`}
      >
        {renderSidebarContent(false)}
      </aside>

      {/* Mobile Drawer */}
      <Drawer open={mobileOpen} onOpenChange={setMobileOpen} placement="left">
        <DrawerContent hideCloseButton className="w-64 p-0">
          {renderSidebarContent(true)}
        </DrawerContent>
      </Drawer>

      {/* Main Content Area */}
      <div className={`transition-all duration-300 ${isCollapsed ? 'lg:pl-20' : 'lg:pl-64'}`}>
        {/* Sticky Header */}
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-border/60 bg-background/80 px-4 backdrop-blur-xl sm:px-6 lg:px-8">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
          >
            <Menu className="size-5" />
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="hidden lg:flex"
            onClick={toggleCollapse}
            title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
          >
            {isCollapsed ? <PanelLeftOpen className="size-5" /> : <PanelLeftClose className="size-5" />}
          </Button>

          <Breadcrumb items={breadcrumbItems} className="hidden sm:flex" />

          <div className="ml-auto flex items-center gap-2.5">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label="Toggle theme"
              title={`Switch to ${theme === 'dark' ? 'Light' : 'Dark'} Mode`}
            >
              {theme === 'dark' ? (
                <Sun className="size-5 text-amber-400" />
              ) : (
                <Moon className="size-5 text-foreground" />
              )}
            </Button>

            <Button type="button" variant="ghost" size="icon" aria-label="Notifications">
              <Bell className="size-5" />
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger>
                <Avatar fallback={initials} alt="Admin profile" className="size-9 border border-primary/40" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <p className="text-xs font-bold text-foreground">
                    {user ? `${user.firstName} ${user.lastName}` : 'Platform Admin'}
                  </p>
                  <p className="text-[10px] text-muted-foreground">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem icon={<LayoutDashboard />} onSelect={() => navigate(appRoutes.admin.dashboard)}>
                  Admin Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem icon={<Settings />} onSelect={() => navigate(appRoutes.admin.settings)}>
                  Account Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem icon={<LogOut />} onSelect={handleLogout} className="text-red-500 font-bold">
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Body */}
        <main className="min-h-[calc(100vh-4rem)]">
          <PageContainer size="full" className="py-8 px-4 sm:px-6 lg:px-8">
            <Outlet />
          </PageContainer>
        </main>
      </div>
    </div>
  )
}
